import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Card,
  CardContent,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import apiClient from "../utils/apiClient";

const soilEffectOptions = ["improves", "depletes", "neutral"];
// Crop types will be fetched from API
const ALL_PERIOD_TYPE_OPTIONS = [
  { value: "pre_germination", label: "Pre-germination" },
  { value: "planting", label: "Planting" },
  { value: "setting", label: "Setting" },
  { value: "direct_sowing", label: "Direct Sowing" },
  { value: "pruning", label: "Pruning" },
  { value: "harvest", label: "Harvest" },
  { value: "picking", label: "Picking" },
];

const CropForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    latin_name: "",
    crop_type_uuid: "",
    cover_crop: null,
    companion: null,
    harvest_min_days: "",
    harvest_max_days: "",
    crop_family_uuid: "",
    plant_spacing_cm: "",
    row_spacing_cm: "",
    soil_effect: "neutral",
    crop_windows: [],
  });

  const [cropFamilies, setCropFamilies] = useState([]);
  const [allCrops, setAllCrops] = useState([]);
  const [cropTypes, setCropTypes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [familiesRes, cropsRes, cropTypesRes] = await Promise.all([
          apiClient.get("/cropfamilies"),
          apiClient.get("/crops"),
          apiClient.get("/croptypes"),
        ]);
        setCropFamilies(familiesRes.data);
        setAllCrops(cropsRes.data);
        setCropTypes(cropTypesRes.data);

        if (initialData) {
          setFormData({
            ...initialData,
            crop_family_uuid: initialData.crop_family?.crop_family_uuid || "",
            crop_type_uuid: initialData.crop_type?.crop_type_uuid || "",
            crop_windows: initialData.crop_windows || [],
            cover_crop: typeof initialData.cover_crop === "undefined" ? null : initialData.cover_crop,
            companion: typeof initialData.companion === "undefined" ? null : initialData.companion,
          });
        }
      } catch (err) {
        console.error("Error fetching form data:", err);
      }
    };
    fetchData();
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddWindow = () => {
    setFormData((prev) => ({
      ...prev,
      crop_windows: [
        ...prev.crop_windows,
        {
          period_type: "planting",
          window_start: "",
          window_end: "",
          notes: "",
          _isNew: true, // Track new windows
        },
      ],
    }));
  };

  const handleWindowChange = (index, field, value) => {
    setFormData((prev) => {
      const newWindows = [...prev.crop_windows];
      newWindows[index] = { ...newWindows[index], [field]: value };
      return { ...prev, crop_windows: newWindows };
    });
  };

  const handleDeleteWindow = async (index) => {
    const window = formData.crop_windows[index];
    
    // If editing existing crop and window has UUID, delete from server
    if (initialData && window.crop_window_uuid) {
      try {
        await apiClient.delete(`/crop-windows/${window.crop_window_uuid}`);
      } catch (err) {
        console.error("Failed to delete window:", err);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      crop_windows: prev.crop_windows.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    // Normalize top-level fields
    const normalized = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => {
        // Skip crop_windows - we'll handle it separately
        if (k === "crop_windows") return [k, v];
        return [k, v === "" ? null : v];
      })
    );

    // Normalize crop_windows array - just clean up empty strings
    const normalizedWindows = (formData.crop_windows || []).map(window => ({
      period_type: window.period_type,
      window_start: window.window_start && window.window_start.trim() !== "" ? window.window_start.trim() : null,
      window_end: window.window_end && window.window_end.trim() !== "" ? window.window_end.trim() : null,
      notes: window.notes && window.notes.trim() !== "" ? window.notes.trim() : null,
      ...(window._isNew && { _isNew: true }),
      ...(window.crop_window_uuid && { crop_window_uuid: window.crop_window_uuid }),
    })).filter(window => window.period_type);

    normalized.crop_windows = normalizedWindows;

    // Set default values for non-vegetable crops
    if (!isVegetable) {
      normalized.soil_effect = "neutral";
    }

    // Backwards-compat: only send cover-crop flags for cover crops.
    // If omitted, backend keeps null/unchanged.
    if (!isCoverCrop) {
      delete normalized.cover_crop;
      delete normalized.companion;
    }

    if (initialData) {
      // For updates, persist crop_windows first so parent refetch gets complete data
      const { crop_windows, ...cropData } = normalized;

      // Handle crop windows: create new ones, update existing ones (before onSubmit)
      for (const window of crop_windows) {
        const windowData = {
          period_type: window.period_type,
          window_start: window.window_start,
          window_end: window.window_end,
          notes: window.notes || null,
          crop_uuid: initialData.crop_uuid,
        };

        if (window._isNew || !window.crop_window_uuid) {
          await apiClient.post(`/crops/${initialData.crop_uuid}/windows`, windowData);
        } else {
          await apiClient.put(`/crop-windows/${window.crop_window_uuid}`, windowData);
        }
      }

      // Then update crop basic data (parent will refetch crops to get full data with windows)
      await onSubmit(cropData);
    } else {
      // For new crops, include windows in payload
      onSubmit(normalized);
    }
  };

  const selectedCropType = cropTypes.find(ct => ct.crop_type_uuid === formData.crop_type_uuid);
  const typeName = selectedCropType?.name || "";

  const isVegetable = typeName === "Vegetable";
  const isCoverCrop = typeName === "Cover crop";
  const showHarvestDays = isVegetable;
  const showPlantSpacing = isVegetable;

  // Filter period types based on selected crop type's allowed_period_types
  const availablePeriodTypes = selectedCropType?.allowed_period_types?.length > 0 
    ? ALL_PERIOD_TYPE_OPTIONS.filter(option => 
        selectedCropType.allowed_period_types.includes(option.value)
      )
    : ALL_PERIOD_TYPE_OPTIONS;

  return (
    <Box>

      <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} margin="normal" size="small" />
      <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} margin="normal" multiline rows={2} size="small" />
      <TextField fullWidth label="Latin Name" name="latin_name" value={formData.latin_name} onChange={handleChange} margin="normal" size="small" />

      <FormControl fullWidth margin="normal" size="small">
        <InputLabel id="crop-type-label">Type</InputLabel>
        <Select labelId="crop-type-label" name="crop_type_uuid" value={formData.crop_type_uuid} onChange={handleChange}>
          {cropTypes.map((cropType) => (
            <MenuItem key={cropType.crop_type_uuid} value={cropType.crop_type_uuid}>
              {cropType.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isCoverCrop && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.cover_crop === true}
                indeterminate={formData.cover_crop == null}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    cover_crop: nextChecked ? true : prev.cover_crop === true ? null : false,
                  }));
                }}
              />
            }
            label="Cover crop (after harvest / fertilizer)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.companion === true}
                indeterminate={formData.companion == null}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    companion: nextChecked ? true : prev.companion === true ? null : false,
                  }));
                }}
              />
            }
            label="Companion / undersown (sow together)"
          />
        </Box>
      )}

      <FormControl fullWidth margin="normal" size="small">
        <InputLabel id="crop-family-label">Crop Family</InputLabel>
        <Select
          labelId="crop-family-label"
          name="crop_family_uuid"
          value={formData.crop_family_uuid}
          onChange={handleChange}
        >
          {cropFamilies.map((family) => (
            <MenuItem key={family.crop_family_uuid} value={family.crop_family_uuid}>
              {family.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal" size="small">
        <InputLabel id="soil-effect-label">Soil Effect</InputLabel>
        <Select
          labelId="soil-effect-label"
          name="soil_effect"
          value={formData.soil_effect}
          onChange={handleChange}
        >
          {soilEffectOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showPlantSpacing && (
        <>
          <TextField fullWidth label="Plant Spacing (cm)" name="plant_spacing_cm" type="number" value={formData.plant_spacing_cm} onChange={handleChange} margin="normal" size="small" />
          <TextField fullWidth label="Row Spacing (cm)" name="row_spacing_cm" type="number" value={formData.row_spacing_cm} onChange={handleChange} margin="normal" size="small" />
        </>
      )}

      {showHarvestDays && (
        <>
          <TextField fullWidth label="Harvest Min Days" name="harvest_min_days" type="number" value={formData.harvest_min_days} onChange={handleChange} margin="normal" size="small" />
          <TextField fullWidth label="Harvest Max Days" name="harvest_max_days" type="number" value={formData.harvest_max_days} onChange={handleChange} margin="normal" size="small" />
        </>
      )}

      {/* Event Periods Section */}
      <Box sx={{ marginTop: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <Typography variant="h6">Event Periods</Typography>
          <Button startIcon={<AddIcon />} onClick={handleAddWindow} variant="outlined" size="small">
            Add Period
          </Button>
        </Box>

        {formData.crop_windows.map((window, index) => (
          <Card key={index} sx={{ marginBottom: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Period {index + 1}
                </Typography>
                <IconButton onClick={() => handleDeleteWindow(index)} size="small" color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Period Type</InputLabel>
                <Select
                  value={window.period_type}
                  onChange={(e) => handleWindowChange(index, "period_type", e.target.value)}
                  label="Period Type"
                >
                  {availablePeriodTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={window.window_start || ""}
                onChange={(e) => handleWindowChange(index, "window_start", e.target.value)}
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={window.window_end || ""}
                onChange={(e) => handleWindowChange(index, "window_end", e.target.value)}
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Notes (optional)"
                value={window.notes || ""}
                onChange={(e) => handleWindowChange(index, "notes", e.target.value)}
                margin="dense"
                size="small"
                multiline
                rows={2}
              />
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <Button onClick={onCancel} className="vintage-button">Cancel</Button>
        <Button variant="outlined" onClick={handleSubmit} className="vintage-button">
          {initialData ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default CropForm;
